import "core-js/fn/array/fill"; // note: required to get working editor in ie11

import { DraftHandleValue, EditorState } from "draft-js";
import Editor from "draft-js-plugins-editor";
import "draft-js/dist/Draft.css";
import * as React from "react";
import { useCallback, useEffect, useRef } from "react";
import { Keys } from "~/common/enums";
import * as editorStyles from "./AdvancedEditor.less";
import { MentionPlugin } from "./types/types";

export type AdvancedEditorProps = {
    state: EditorState;
    onChange: (newState: EditorState) => void;
    onFocusChange: (isFocused: boolean) => void;
    post: F<void>;
    isFocused: boolean;
    placeholder?: string;
    handleKeyCommand?: (command: string, editorState: EditorState, eventTimeStamp: number) => DraftHandleValue;
    blockRenderMap?: Draft.DraftBlockRenderMap;
    readonly?: boolean;
    handlePastedText?: (text: string, html: string, editorState: EditorState) => DraftHandleValue;
    handleBeforeInput?: (chars: string, editorState: EditorState, eventTimeStamp: number) => DraftHandleValue;
} & MentionPlugin;

const useFocusEditor = (hasFocus: boolean, editorRef: React.MutableRefObject<Editor>) => {
    // set focus to editor if hasFocus changed to true, but postpone it to
    // next next rendering to avoid race conditions with updating editorState
    // (mentions plugin doesn't work if has focus is set on first rendering)

    const [focusTrigger, setFocusTrigger] = React.useState(0);

    useEffect(() => {
        if (hasFocus) {
            setFocusTrigger(prev => ++prev);
        }
    }, [hasFocus]);

    useEffect(() => {
        if (focusTrigger > 0) {
            editorRef.current?.focus();
        }
    }, [focusTrigger]);
};

export const AdvancedEditor: React.FC<AdvancedEditorProps> = ({
    state,
    onChange,
    onFocusChange,
    post,
    plugin,
    isFocused,
    placeholder,
    handleKeyCommand,
    blockRenderMap,
    handlePastedText,
    handleBeforeInput,
    readonly,
}) => {
    const editor = useRef<Editor>();

    const onBlur = useCallback(() => {
        onFocusChange(false);
    }, [onFocusChange]);

    const onFocus = useCallback(() => {
        onFocusChange(true);
    }, [onFocusChange]);

    const ctrlEnterHandled = useRef(false);
    const handleCtrlEnter = useCallback(
        (e: React.KeyboardEvent): DraftHandleValue => {
            if (e.ctrlKey) {
                ctrlEnterHandled.current = true;
                return "handled";
            }
        },
        [post]
    );

    // post on keyup just to avoid max length warning auto closing
    const onKeyUp = useCallback(
        (e: React.KeyboardEvent) => {
            if (readonly) {
                return;
            }

            if ((e.ctrlKey && e.key === Keys.Enter) || ctrlEnterHandled.current) {
                ctrlEnterHandled.current = false;
                post();
            }
        },
        [post]
    );

    const plugins = React.useMemo(() => (plugin ? [plugin] : []), [plugin]);

    useFocusEditor(isFocused, editor);

    return (
        <div className={editorStyles.editor} onKeyUp={onKeyUp}>
            <Editor
                tabIndex={0}
                editorState={state}
                onChange={onChange}
                ref={editor}
                onBlur={onBlur}
                handlePastedText={handlePastedText}
                onFocus={onFocus}
                handleReturn={handleCtrlEnter}
                plugins={plugins}
                placeholder={placeholder}
                stripPastedStyles={true}
                handleBeforeInput={handleBeforeInput}
                blockRenderMap={blockRenderMap}
                handleKeyCommand={handleKeyCommand}
                readOnly={readonly}
            />
        </div>
    );
};
